import type { ADXLines } from "../../model/calcADX";
import type { ADXSettings } from "../../model";
import { COLORS, GRID } from "features/chart";

const TOP = 8;
const BOTTOM = 20;

export function drawADX(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  lines: ADXLines,
  visibleStart: number,
  visibleEnd: number,
  settings: ADXSettings,
): void {
  const { left, right } = GRID;
  const chartW = width - left - right;
  const chartH = height - TOP - BOTTOM;

  ctx.fillStyle = COLORS.bg;
  ctx.fillRect(0, 0, width, height);

  const startIdx = Math.floor(visibleStart);
  const endIdx = Math.ceil(visibleEnd);
  const cw = chartW / (Math.floor(visibleEnd) - startIdx + 1);

  let minVal = Infinity, maxVal = -Infinity;
  const allLines = [lines.adx, lines.plusDI, lines.minusDI];
  for (const arr of allLines) {
    for (let i = startIdx; i <= endIdx; i++) {
      const v = arr[i];
      if (v === null || v === undefined) continue;
      if (v < minVal) minVal = v;
      if (v > maxVal) maxVal = v;
    }
  }
  if (minVal === Infinity) { minVal = 0; maxVal = 100; }
  const pad = (maxVal - minVal || 1) * 0.1;
  minVal = Math.max(0, minVal - pad);
  maxVal = maxVal + pad;
  const range = maxVal - minVal;
  const valToY = (v: number) => TOP + ((maxVal - v) / range) * chartH;

  ctx.font = "10px sans-serif";
  const lineEndX = width - right - ctx.measureText(maxVal.toFixed(1)).width - 6;

  ctx.strokeStyle = COLORS.grid;
  ctx.lineWidth = 1;
  for (let i = 0; i <= 2; i++) {
    const y = TOP + (chartH * i) / 2;
    ctx.beginPath();
    ctx.moveTo(left, y);
    ctx.lineTo(lineEndX, y);
    ctx.stroke();
  }

  const drawLine = (values: (number | null)[], color: string, lw: number) => {
    ctx.strokeStyle = color;
    ctx.lineWidth = lw;
    ctx.beginPath();
    let started = false;
    for (let i = startIdx; i <= endIdx; i++) {
      const v = values[i];
      if (v === null || v === undefined) { started = false; continue; }
      const x = left + (i - startIdx + 0.5) * cw;
      const y = valToY(v);
      if (!started) { ctx.moveTo(x, y); started = true; }
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
  };

  if (settings.adx.visible) drawLine(lines.adx, settings.adx.color, settings.adx.width);
  if (settings.plusDI.visible) drawLine(lines.plusDI, settings.plusDI.color, settings.plusDI.width);
  if (settings.minusDI.visible) drawLine(lines.minusDI, settings.minusDI.color, settings.minusDI.width);

  ctx.fillStyle = COLORS.label;
  ctx.textAlign = "right";
  ctx.textBaseline = "middle";
  const scaleLabels = [maxVal, minVal + range / 2, minVal];
  for (const v of scaleLabels) {
    const y = valToY(v);
    if (y < TOP || y > height - BOTTOM) continue;
    ctx.fillText(v % 1 === 0 ? String(Math.round(v)) : v.toFixed(1), width - right + 2, y);
  }
}
